import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActions,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  FormControlLabel,
  Switch,
  InputAdornment,
} from '@mui/material'
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Settings as SettingsIcon,
  Info as InfoIcon,
  People as PeopleIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  AttachFile as AttachFileIcon,
  AccountTree as AccountTreeIcon,
  Label as LabelIcon,
  Search as SearchIcon,
} from '@mui/icons-material'
import { agentsAPI, usersAPI } from '../services/api'
import AdminPageWrapper from '../components/AdminPageWrapper'
import LoadingSpinner from '../components/LoadingSpinner'
import ErrorAlert from '../components/ErrorAlert'

const AdminAgentsPage = () => {
  const [agents, setAgents] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [selectedAgent, setSelectedAgent] = useState(null)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [loadingAgentDetails, setLoadingAgentDetails] = useState(false)

  // Filter state
  const [searchFilter, setSearchFilter] = useState('')
  const [showSubAgents, setShowSubAgents] = useState(false)

  // User management state
  const [usersDialogOpen, setUsersDialogOpen] = useState(false)
  const [selectedAgentForUsers, setSelectedAgentForUsers] = useState(null)
  const [allUsers, setAllUsers] = useState([])
  const [agentUsers, setAgentUsers] = useState([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [selectedUserToAdd, setSelectedUserToAdd] = useState(null)

  useEffect(() => {
    loadAgents()
  }, [showSubAgents]) // Reload when showSubAgents changes

  const loadAgents = async () => {
    try {
      setLoading(true)
      setError(null)
      // Fetch agents from backend with sub-agent filtering handled by API
      // minimal=true for faster response
      // include_sub_agents controlled by showSubAgents state
      const response = await agentsAPI.getAgents(true, showSubAgents)
      setAgents(response.agents || [])
    } catch (err) {
      console.error('Failed to load agents:', err)
      setError('Failed to load agents: ' + (err.response?.data?.detail || err.message))
    } finally {
      setLoading(false)
    }
  }

  // Filter agents based on search text only (sub-agent filtering handled by API)
  const filteredAgents = agents.filter(agent => {
    // Filter by search text
    if (searchFilter) {
      const searchLower = searchFilter.toLowerCase()
      return (
        agent.name.toLowerCase().includes(searchLower) ||
        (agent.description && agent.description.toLowerCase().includes(searchLower))
      )
    }

    return true
  })

  const refreshAgentSchemas = async () => {
    try {
      setError(null)
      await agentsAPI.refreshAgentSchemas()
      await loadAgents()
    } catch (err) {
      console.error('Failed to refresh agent schemas:', err)
      setError('Failed to refresh agent schemas: ' + (err.response?.data?.detail || err.message))
    }
  }

  const showAgentDetails = async (agent) => {
    try {
      setLoadingAgentDetails(true)
      setDetailsDialogOpen(true)
      setError(null)

      // Fetch full agent details when user clicks "Details"
      const fullAgentData = await agentsAPI.getAgentInfo(agent.name)
      setSelectedAgent(fullAgentData)
    } catch (err) {
      console.error('Failed to load agent details:', err)
      setError('Failed to load agent details: ' + (err.response?.data?.detail || err.message))
      setDetailsDialogOpen(false)
    } finally {
      setLoadingAgentDetails(false)
    }
  }

  const showAgentUsers = async (agent) => {
    try {
      setLoadingUsers(true)
      setUsersDialogOpen(true)
      setSelectedAgentForUsers(agent)
      setError(null)

      // Fetch all users
      const usersResponse = await usersAPI.listUsers({}, 1, 1000)
      const users = usersResponse.users || []
      setAllUsers(users)

      // Filter users who have access to this agent
      const usersWithAccess = users.filter(user =>
        user.assigned_agents && user.assigned_agents.includes(agent.name)
      )
      setAgentUsers(usersWithAccess)
    } catch (err) {
      console.error('Failed to load users:', err)
      setError('Failed to load users: ' + (err.response?.data?.detail || err.message))
      setUsersDialogOpen(false)
    } finally {
      setLoadingUsers(false)
    }
  }

  const addUserToAgent = async () => {
    if (!selectedUserToAdd || !selectedAgentForUsers) return

    try {
      setError(null)

      // Fetch CURRENT user data from API to get up-to-date agent assignments
      const currentUserData = await usersAPI.getUser(selectedUserToAdd.email)
      const currentAgents = currentUserData.assigned_agents || []

      // Add the new agent if not already assigned
      if (!currentAgents.includes(selectedAgentForUsers.name)) {
        const updatedAgents = [...currentAgents, selectedAgentForUsers.name]
        await usersAPI.updateUser(selectedUserToAdd.email, { assigned_agents: updatedAgents })

        // Refresh the user list
        await showAgentUsers(selectedAgentForUsers)
        setSelectedUserToAdd(null)
      }
    } catch (err) {
      console.error('Failed to add user to agent:', err)
      setError('Failed to add user to agent: ' + (err.response?.data?.detail || err.message))
    }
  }

  const removeUserFromAgent = async (user) => {
    if (!selectedAgentForUsers) return

    try {
      setError(null)

      // Fetch CURRENT user data from API to get up-to-date agent assignments
      const currentUserData = await usersAPI.getUser(user.email)
      const currentAgents = currentUserData.assigned_agents || []

      // Remove the agent from user's assignments
      const updatedAgents = currentAgents.filter(
        agentName => agentName !== selectedAgentForUsers.name
      )
      await usersAPI.updateUser(user.email, { assigned_agents: updatedAgents })

      // Refresh the user list
      await showAgentUsers(selectedAgentForUsers)
    } catch (err) {
      console.error('Failed to remove user from agent:', err)
      setError('Failed to remove user from agent: ' + (err.response?.data?.detail || err.message))
    }
  }

  const getAgentStatusColor = (agent) => {
    if (agent.endpoint?.startsWith('local://')) return 'success'
    if (agent.endpoint?.startsWith('http://')) return 'warning'
    if (agent.endpoint?.startsWith('https://')) return 'primary'
    return 'default'
  }

  const getAgentStatusText = (agent) => {
    if (agent.endpoint?.startsWith('local://')) return 'Local'
    if (agent.endpoint?.startsWith('http://')) return 'HTTP'
    if (agent.endpoint?.startsWith('https://')) return 'HTTPS'
    return 'Unknown'
  }

  if (loading && agents.length === 0) {
    return <LoadingSpinner />
  }

  return (
    <AdminPageWrapper title="Agent Management">
      {/* Filters and Actions */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'center' }}>
        <TextField
          placeholder="Search agents..."
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
          size="small"
          sx={{ flexGrow: 1, minWidth: '250px' }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={
            <Switch
              checked={showSubAgents}
              onChange={(e) => setShowSubAgents(e.target.checked)}
              color="primary"
            />
          }
          label="Show Sub-Agents"
        />
        <Tooltip title="Refresh agent schemas from code">
          <IconButton onClick={refreshAgentSchemas} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <ErrorAlert
          message={error}
          onClose={() => setError(null)}
          sx={{ mb: 3 }}
        />
      )}

      {/* Results Summary */}
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Showing {filteredAgents.length} of {agents.length} agents
        {!showSubAgents && ` (sub-agents hidden)`}
      </Typography>

      <Grid container spacing={3}>
        {filteredAgents.map((agent) => (
          <Grid item xs={12} md={6} lg={4} key={agent.name}>
            <Card elevation={2}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="h2">
                    {agent.name}
                  </Typography>
                  <Chip
                    label={getAgentStatusText(agent)}
                    color={getAgentStatusColor(agent)}
                    size="small"
                  />
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {agent.description || 'No description available'}
                </Typography>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Primary Model:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {agent.model_name || (agent.default_primary_model ? `Using default (${agent.default_primary_model})` : 'Using default')}
                  </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    Simple Model:
                  </Typography>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {agent.simple_model_name || (agent.default_simple_model ? `Using default (${agent.default_simple_model})` : 'Using default')}
                  </Typography>
                </Box>

                {agent.schema?.required_fields && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Required Fields:
                    </Typography>
                    <Box sx={{ mt: 0.5 }}>
                      {agent.schema.required_fields.map((field) => (
                        <Chip
                          key={field}
                          label={field}
                          size="small"
                          variant="outlined"
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  </Box>
                )}

                {/* Capability Indicators */}
                <Box sx={{ display: 'flex', gap: 2, pt: 1, borderTop: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
                  <Tooltip title={agent.allow_multi_user ? "Multi-user enabled - any user with agent permissions can respond" : "Multi-user disabled - only assigned user can respond"}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <GroupIcon
                        sx={{
                          fontSize: '1.2rem',
                          color: agent.allow_multi_user ? 'success.main' : 'action.disabled'
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: agent.allow_multi_user ? 'success.main' : 'text.disabled',
                          fontWeight: agent.allow_multi_user ? 600 : 400
                        }}
                      >
                        Multi-user
                      </Typography>
                    </Box>
                  </Tooltip>

                  <Tooltip title={agent.has_forms ? "Supports structured forms" : "No structured forms"}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AssignmentIcon
                        sx={{
                          fontSize: '1.2rem',
                          color: agent.has_forms ? 'success.main' : 'action.disabled'
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: agent.has_forms ? 'success.main' : 'text.disabled',
                          fontWeight: agent.has_forms ? 600 : 400
                        }}
                      >
                        Forms
                      </Typography>
                    </Box>
                  </Tooltip>

                  <Tooltip title={agent.accepts_file_uploads ? "Accepts file uploads" : "No file uploads"}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AttachFileIcon
                        sx={{
                          fontSize: '1.2rem',
                          color: agent.accepts_file_uploads ? 'success.main' : 'action.disabled'
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: agent.accepts_file_uploads ? 'success.main' : 'text.disabled',
                          fontWeight: agent.accepts_file_uploads ? 600 : 400
                        }}
                      >
                        File Uploads
                      </Typography>
                    </Box>
                  </Tooltip>

                  <Tooltip title={agent.is_sub_agent ? "This is a sub-agent" : "Not a sub-agent"}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <LabelIcon
                        sx={{
                          fontSize: '1.2rem',
                          color: agent.is_sub_agent ? 'success.main' : 'action.disabled'
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: agent.is_sub_agent ? 'success.main' : 'text.disabled',
                          fontWeight: agent.is_sub_agent ? 600 : 400
                        }}
                      >
                        Sub-Agent
                      </Typography>
                    </Box>
                  </Tooltip>

                  <Tooltip title={agent.has_sub_agents ? "This agent has sub-agents" : "No sub-agents"}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <AccountTreeIcon
                        sx={{
                          fontSize: '1.2rem',
                          color: agent.has_sub_agents ? 'success.main' : 'action.disabled'
                        }}
                      />
                      <Typography
                        variant="caption"
                        sx={{
                          color: agent.has_sub_agents ? 'success.main' : 'text.disabled',
                          fontWeight: agent.has_sub_agents ? 600 : 400
                        }}
                      >
                        Has Sub-Agents
                      </Typography>
                    </Box>
                  </Tooltip>
                </Box>
              </CardContent>

              <CardActions>
                <Button
                  size="small"
                  startIcon={<InfoIcon />}
                  onClick={() => showAgentDetails(agent)}
                >
                  Details
                </Button>
                <Button
                  size="small"
                  startIcon={<PeopleIcon />}
                  onClick={() => showAgentUsers(agent)}
                >
                  Users
                </Button>
                <Button
                  size="small"
                  startIcon={<SettingsIcon />}
                  disabled
                >
                  Configure
                </Button>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {filteredAgents.length === 0 && !loading && (
        <Alert severity="info" sx={{ mt: 3 }}>
          {agents.length === 0
            ? 'No agents found. Try refreshing the agent schemas.'
            : 'No agents match your filters. Try adjusting your search or toggle sub-agents.'}
        </Alert>
      )}

      {/* Agent Users Dialog */}
      <Dialog
        open={usersDialogOpen}
        onClose={() => setUsersDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Users with Access to {selectedAgentForUsers?.name}
        </DialogTitle>
        <DialogContent>
          {loadingUsers ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <LoadingSpinner />
            </Box>
          ) : (
            <Box sx={{ mt: 2 }}>
              {/* Add User Section */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Add User to Agent
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Autocomplete
                    fullWidth
                    options={allUsers.filter(u => !agentUsers.some(au => au.email === u.email))}
                    getOptionLabel={(option) => `${option.email}${option.display_name ? ` (${option.display_name})` : ''}`}
                    value={selectedUserToAdd}
                    onChange={(event, newValue) => setSelectedUserToAdd(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Search users"
                        placeholder="Type to search..."
                        size="small"
                      />
                    )}
                  />
                  <Button
                    variant="contained"
                    onClick={addUserToAgent}
                    disabled={!selectedUserToAdd}
                  >
                    Add
                  </Button>
                </Box>
              </Box>

              {/* Current Users List */}
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Current Users ({agentUsers.length})
              </Typography>
              {agentUsers.length === 0 ? (
                <Alert severity="info">
                  No users have access to this agent yet.
                </Alert>
              ) : (
                <Paper variant="outlined">
                  <List>
                    {agentUsers.map((user) => (
                      <ListItem key={user.email} divider>
                        <ListItemText
                          primary={user.email}
                          secondary={user.display_name || 'No display name'}
                        />
                        <ListItemSecondaryAction>
                          <Tooltip title="Remove user access">
                            <IconButton
                              edge="end"
                              onClick={() => removeUserFromAgent(user)}
                              size="small"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </Paper>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsersDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Agent Details Dialog */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Agent Details: {selectedAgent?.name}
        </DialogTitle>
        <DialogContent>
          {loadingAgentDetails ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <LoadingSpinner />
            </Box>
          ) : selectedAgent && (
            <Box>
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">Basic Information</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell><strong>Name</strong></TableCell>
                          <TableCell>{selectedAgent.name}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Description</strong></TableCell>
                          <TableCell>{selectedAgent.description || 'No description'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Primary Model</strong></TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {selectedAgent.model_name || (selectedAgent.default_primary_model ? `Using default (${selectedAgent.default_primary_model})` : 'Using default')}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Simple Model</strong></TableCell>
                          <TableCell sx={{ fontFamily: 'monospace' }}>
                            {selectedAgent.simple_model_name || (selectedAgent.default_simple_model ? `Using default (${selectedAgent.default_simple_model})` : 'Using default')}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Multi-user</strong></TableCell>
                          <TableCell>
                            <Chip
                              label={selectedAgent.allow_multi_user ? 'Enabled' : 'Disabled'}
                              color={selectedAgent.allow_multi_user ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Forms Support</strong></TableCell>
                          <TableCell>
                            <Chip
                              label={selectedAgent.has_forms ? 'Yes' : 'No'}
                              color={selectedAgent.has_forms ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>File Uploads</strong></TableCell>
                          <TableCell>
                            <Chip
                              label={selectedAgent.accepts_file_uploads ? 'Accepted' : 'Not Accepted'}
                              color={selectedAgent.accepts_file_uploads ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Created</strong></TableCell>
                          <TableCell>{selectedAgent.created_at ? new Date(selectedAgent.created_at).toLocaleString() : 'Unknown'}</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>Last Updated</strong></TableCell>
                          <TableCell>{selectedAgent.modified_at ? new Date(selectedAgent.modified_at).toLocaleString() : 'Unknown'}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </AccordionDetails>
              </Accordion>

              {selectedAgent.schema && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Schema</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                      <pre style={{ 
                        margin: 0, 
                        fontSize: '0.85rem', 
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {JSON.stringify(selectedAgent.schema, null, 2)}
                      </pre>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {selectedAgent.config && Object.keys(selectedAgent.config).length > 0 && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Configuration</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                      <pre style={{
                        margin: 0,
                        fontSize: '0.85rem',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {JSON.stringify(selectedAgent.config, null, 2)}
                      </pre>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}

              {selectedAgent.agent_config && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="h6">Agent Configuration (Environment-Specific)</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1 }}>
                      <pre style={{
                        margin: 0,
                        fontSize: '0.85rem',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word'
                      }}>
                        {JSON.stringify(selectedAgent.agent_config, null, 2)}
                      </pre>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </AdminPageWrapper>
  )
}

export default AdminAgentsPage